/**
 * Public entry point for the exercise generator.
 *
 * Given an item from the catalog and a seed, returns a fully-realized exercise
 * ready for the audio engine and UI to consume.
 *
 * The signature is intentionally minimal: the caller doesn't need to know
 * about generators, just items.
 */

import type { Exercise } from "./exercise.js";
import { generateScaleDegree } from "./generators/scale-degree.js";
import { generateInterval } from "./generators/interval.js";
import { generateChordQuality } from "./generators/chord-quality.js";
import { generateChordInversion } from "./generators/chord-inversion.js";
import { generateProgression, generateCadence } from "./generators/progression.js";
import { generateMode } from "./generators/mode.js";
import { generateSlashChord } from "./generators/slash-chord.js";
import { generateSecondaryDominant } from "./generators/secondary-dominant.js";
import { generateModulation, type ModulationRelationship } from "./generators/modulation.js";
import { generateMelodicDictation } from "./generators/melodic-dictation.js";
import { generateFretInterval } from "./exercise-fret-interval.js";
import { generateFretTriad } from "./exercise-fret-triad.js";
import { generateFretCaged, generateFretCagedAnchoring } from "./exercise-fret-caged.js";
import { generateFretDiatonic, type DiatonicDegree } from "./exercise-fret-diatonic.js";
import { generateFretDiatonicMinor, type MinorDiatonicDegree } from "./exercise-fret-diatonic-minor.js";
import { generateFretSeventh, type SeventhQuality } from "./exercise-fret-seventh.js";
import { generateFretScaleDegree, type FretScaleType } from "./exercise-fret-scale-degree.js";
import { generateFretScaleDegreeCaged } from "./exercise-fret-scale-degree-caged.js";
import { generateRhythmicDictation } from "./generators/rhythmic-dictation.js";
import type { CagedShape } from "@music-app/theory-engine";
import type { CadenceOptions, Mode } from "./utils.js";
import type { CadenceTempo } from "./exercise-cadence.js";

export const CADENCE_TEMPO_OPTS: Record<CadenceTempo, Pick<CadenceOptions, "chordDuration" | "gap">> = {
  slow:   { chordDuration: 0.85, gap: 0.05 }, // default — ~3.7 s total
  medium: { chordDuration: 0.60, gap: 0.05 }, // brisk   — ~2.6 s total
  fast:   { chordDuration: 0.40, gap: 0.04 }, // snappy  — ~1.8 s total
};

export interface GenerateOptions {
  cadenceTempo?: CadenceTempo;
}

/**
 * Minimal item shape we need. This matches the Item type from the
 * learning-system package — duplicated here to avoid a hard dependency
 * (the packages compose loosely).
 */
export interface ItemRef {
  id: string;
  exerciseType:
    | "scale-degree-recognition"
    | "interval-recognition"
    | "chord-quality-recognition"
    | "chord-inversion-recognition"
    | "progression-recognition"
    | "cadence-recognition"
    | "mode-recognition"
    | "slash-chord-recognition"
    | "secondary-dominant-recognition"
    | "modulation-recognition"
    | "melodic-dictation"
    | "rhythmic-dictation"
    | "fret-interval-recognition"
    | "fret-triad-recognition"
    | "fret-triad-inversion-recognition"
    | "fret-caged-recognition"
    | "fret-diatonic-minor-recognition"
    | "fret-seventh-recognition"
    | "fret-scale-degree-recognition"
    | "fret-scale-degree-caged-recognition"
    | string;
  params: Record<string, string | number>;
}

export function generate(item: ItemRef, seed: number = Date.now(), opts?: GenerateOptions): Exercise {
  const cadenceOpts: CadenceOptions | undefined = opts?.cadenceTempo
    ? CADENCE_TEMPO_OPTS[opts.cadenceTempo]
    : undefined;

  switch (item.exerciseType) {
    case "scale-degree-recognition":
      return generateScaleDegree(
        item.id,
        {
          degree: item.params.degree as number,
          mode: (item.params.mode as "major" | "minor") ?? "major",
          ...(item.params.alteration === "♯" || item.params.alteration === "♭"
            ? { alteration: item.params.alteration }
            : {}),
        },
        seed,
        cadenceOpts,
      );
    case "interval-recognition":
      return generateInterval(
        item.id,
        {
          interval: item.params.interval as string,
          context: (item.params.context as "major" | "minor" | "chromatic") ?? "major",
          direction: (item.params.direction as "ascending" | "descending") ?? "ascending",
        },
        seed,
        cadenceOpts,
      );
    case "chord-quality-recognition":
      return generateChordQuality(
        item.id,
        { quality: item.params.quality as string },
        seed,
      );
    case "chord-inversion-recognition":
      return generateChordInversion(
        item.id,
        {
          quality: item.params.quality as string,
          inversion: item.params.inversion as number,
        },
        seed,
      );
    case "progression-recognition":
      return generateProgression(
        item.id,
        { progression: item.params.progression as string },
        seed,
      );
    case "cadence-recognition":
      return generateCadence(
        item.id,
        { cadence: item.params.cadence as string },
        seed,
      );
    case "mode-recognition":
      return generateMode(
        item.id,
        { mode: item.params.mode as Mode },
        seed,
      );
    case "slash-chord-recognition":
      return generateSlashChord(
        item.id,
        {
          upperRoot: item.params.upperRoot as string,
          upperQuality: item.params.upperQuality as string,
          bass: item.params.bass as string,
        },
        seed,
      );
    case "secondary-dominant-recognition":
      return generateSecondaryDominant(
        item.id,
        { target: item.params.target as number },
        seed,
      );
    case "modulation-recognition":
      return generateModulation(
        item.id,
        { relationship: item.params.relationship as ModulationRelationship },
        seed,
      );
    case "rhythmic-dictation":
      return generateRhythmicDictation(
        item.id,
        {
          pattern: item.params.pattern as string,
          meter: (item.params.meter as "4/4" | "6/8") ?? "4/4",
          figure: (item.params.figure as string) ?? "",
        },
        seed,
      );
    case "melodic-dictation":
      return generateMelodicDictation(
        item.id,
        {
          length: item.params.length as number,
          range: (item.params.range as "narrow" | "wide") ?? "narrow",
          // Encoded as 1/0 in the item library so Item.params stays
          // (string | number)-typed without a boolean widening.
          chromatic: item.params.chromatic === 1 || item.params.chromatic === "true",
        },
        seed,
        cadenceOpts,
      );
    case "fret-interval-recognition":
      return generateFretInterval(
        item.id,
        {
          interval: item.params.interval as string,
          shapeFamily: item.params.shapeFamily as "standard" | "gb-offset" | "octave" | undefined,
          octaveShapeSymbol: item.params.octaveShapeSymbol as string | undefined,
        },
        seed,
        cadenceOpts,
      );
    case "fret-triad-recognition":
      return generateFretTriad(
        item.id,
        {
          quality: item.params.quality as string,
          stringSet: item.params.stringSet as number,
          inversion: (item.params.inversion as number) ?? 0,
        },
        seed,
        "shape",
      );
    case "fret-triad-inversion-recognition":
      return generateFretTriad(
        item.id,
        {
          quality: item.params.quality as string,
          stringSet: item.params.stringSet as number,
          inversion: item.params.inversion as number,
        },
        seed,
        "inversion",
      );
    case "fret-caged-recognition":
      return generateFretCaged(
        item.id,
        {
          shape: item.params.shape as CagedShape,
          quality: item.params.quality as "maj" | "min",
        },
        seed,
        cadenceOpts,
      );
    case "fret-caged-anchoring":
      return generateFretCagedAnchoring(
        item.id,
        {
          shape: item.params.shape as CagedShape,
          quality: item.params.quality as "maj" | "min",
        },
        seed,
        cadenceOpts,
      );
    case "fret-diatonic-recognition":
      return generateFretDiatonic(
        item.id,
        {
          shape: item.params.shape as CagedShape,
          degree: item.params.degree as DiatonicDegree,
        },
        seed,
        cadenceOpts,
      );
    case "fret-diatonic-minor-recognition":
      return generateFretDiatonicMinor(
        item.id,
        {
          shape: item.params.shape as CagedShape,
          degree: item.params.degree as MinorDiatonicDegree,
        },
        seed,
        cadenceOpts,
      );
    case "fret-seventh-recognition":
      return generateFretSeventh(
        item.id,
        {
          shape: item.params.shape as CagedShape,
          quality: item.params.quality as SeventhQuality,
        },
        seed,
        cadenceOpts,
      );
    case "fret-scale-degree-recognition":
      return generateFretScaleDegree(
        item.id,
        {
          degree: item.params.degree as number,
          mode: (item.params.mode as FretScaleType | undefined) ?? "major",
          octaves: item.params.octaves as number | undefined,
        },
        seed,
        cadenceOpts,
      );
    case "fret-scale-degree-caged-recognition":
      return generateFretScaleDegreeCaged(
        item.id,
        {
          shape: item.params.shape as CagedShape,
          degree: item.params.degree as number,
        },
        seed,
        cadenceOpts,
      );
    default:
      throw new Error(`No generator registered for exerciseType: ${item.exerciseType}`);
  }
}

/**
 * Grade a user's answer. Returns whether they were correct, plus the
 * explanation to show.
 */
export function gradeAnswer(
  exercise: Exercise,
  selectedOptionId: string,
): { correct: boolean; explanation: string } {
  return {
    correct: selectedOptionId === exercise.correctAnswerId,
    explanation: exercise.meta.explanation,
  };
}
